import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import Cookies from 'js-cookie';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { camelize } from '../../util/string';
import {
  BUSINESS_USER_TYPE,
  isBusinessUser,
  isInviteOnlyUserType,
  publicUserTypes,
  readInviteToken,
} from '../../util/userTypes';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import { ensureCurrentUser, getFeaturedListingsProps } from '../../util/data';
import { apiBaseUrl } from '../../util/api';
import {
  isSignupEmailTakenError,
  isTooManyEmailVerificationRequestsError,
} from '../../util/errors';

import { login, authenticationInProgress, signup, signupWithIdp } from '../../ducks/auth.duck';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { fetchFeaturedListings } from '../../ducks/featuredListings.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';

import {
  Page,
  Heading,
  IconSpinner,
  NamedLink,
  NamedRedirect,
  Modal,
  LayoutSingleColumn,
  PartnerBadge,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
// We need to get ToS asset and get it rendered for the modal on this page.
// We need to get PrivacyPolicy asset and get it rendered for the modal on this page.
import { PrivacyPolicyContent } from '../../containers/PrivacyPolicyPage/PrivacyPolicyPage';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';
import LandingPage from '../../containers/LandingPage/LandingPage';

import {
  getAuthInfoFromCookies,
  getAuthErrorFromCookies,
  getHandleSubmitConfirm,
  getHandleSubmitSignup,
} from './AuthenticationPage.helpers';

import TermsAndConditions from './TermsAndConditions/TermsAndConditions';
import ConfirmSignupForm from './ConfirmSignupForm/ConfirmSignupForm';
import LoginForm from './LoginForm/LoginForm';
import SignupForm from './SignupForm/SignupForm';
import WelcomeForm from './WelcomeForm/WelcomeForm';
import OnboardingWelcome from './OnboardingWelcome/OnboardingWelcome';
import { markOnboardingNudgeShown } from './AuthenticationPage.duck';
import EmailVerificationInfo from './EmailVerificationInfo';
import SocialAuthButtons from './SocialAuthButtons/SocialAuthButtons';

import { PRIVACY_POLICY_ASSET_NAME } from './AuthenticationPage.duck';

import css from './AuthenticationPage.module.css';

// Read an `email` query param off the URL safely on both server and client.
const readEmailFromSearch = search => {
  if (!search) return null;
  const trimmed = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(trimmed);
  return params.get('email');
};

const AuthenticationFormErrorMessage = props => {
  const { isLogin, idpAuthError, loginError, signupError } = props;

  const translationId =
    isLogin && !!idpAuthError
      ? 'AuthenticationPage.idpAuthFailed'
      : isLogin && !!loginError
      ? 'AuthenticationPage.loginFailed'
      : !!signupError && isSignupEmailTakenError(signupError)
      ? 'AuthenticationPage.signupFailedEmailAlreadyTaken'
      : !!signupError
      ? 'AuthenticationPage.signupFailed'
      : null;

  return translationId ? (
    <div className={css.error}>
      <FormattedMessage id={translationId} />
    </div>
  ) : null;
};

const ResendVerificationErrorMessage = props => {
  const { sendVerificationEmailError } = props;

  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'AuthenticationPage.resendFailedTooManyRequests'
    : 'AuthenticationPage.resendFailed';

  return sendVerificationEmailError ? (
    <p className={css.error}>
      <FormattedMessage id={resendErrorTranslationId} />
    </p>
  ) : null;
};

const BlankPage = props => {
  const { schemaTitle, schemaDescription, scrollingDisabled, topbarClasses } = props;
  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      // Login and sign-up are not pages to land on from Google (Guro saw them
      // as sitelinks under zuprize.no).
      noIndex
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer className={topbarClasses} />}
        footer={<FooterContainer />}
      >
        <div className={css.spinnerContainer}>
          <IconSpinner />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The AuthenticationPage component.
 *
 * Tabs:
 *   - 'welcome'   → email-gateway entry (/login). Vipps + Google +
 *                   Facebook social buttons, "ELLER" divider, email
 *                   field that routes to /signup?email=… on submit.
 *   - 'signup'    → full signup form (/signup). Email may be pre-filled
 *                   via ?email=…
 *   - 'login'     → email + password login form (/login/password).
 *                   Reached when an existing user clicks "Logg inn"
 *                   from the signup form. Email may be pre-filled.
 *   - 'confirm'   → IdP signup-confirmation step (/confirm).
 *   - 'onboarding'→ post-signup welcome with onboarding checklist
 *                   (/get-started). Routed to from signup success.
 *
 * @component
 */
export const AuthenticationPageComponent = props => {
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [authInfo, setAuthInfo] = useState(getAuthInfoFromCookies());
  const [authError, setAuthError] = useState(getAuthErrorFromCookies());
  const [mounted, setMounted] = useState(false);

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const history = useHistory();
  const intl = useIntl();

  useEffect(() => {
    // Remove the autherror cookie once the content is saved to state
    // because we don't want to show the error message e.g. after page refresh
    if (authError) {
      Cookies.remove('st-autherror');
    }
    setMounted(true);
  }, []);

  // On mobile, it's better to scroll to top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [privacyModalOpen]);

  const {
    authInProgress,
    currentUser,
    isAuthenticated,
    location,
    params: pathParams,
    loginError,
    scrollingDisabled,
    signupError,
    confirmError,
    submitLogin,
    submitSignup,
    submitSingupWithIdp,
    tab = 'welcome',
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    onOnboardingNudgeShown,
    onManageDisableScrolling,
    pageAssetsData,
    pageAssetsFetchInProgress,
    pageAssetsFetchError,
    invite,
    staticContext,
  } = props;

  // History API has potentially state tied to this route
  // We have used that state to store previous URL ("from"),
  // so that use can be redirected back to that page after authentication.
  const locationFrom = location.state?.from || null;
  const authinfoFrom = authInfo?.from || null;
  const from = locationFrom || authinfoFrom || null;
  const idp = authInfo ? authInfo.idpId.replace(/^./, str => str.toUpperCase()) : null;

  const isWelcome = tab === 'welcome';
  const isConfirm = tab === 'confirm';
  const isLogin = tab === 'login';
  const isSignup = tab === 'signup';
  const isOnboarding = tab === 'onboarding';

  const userTypeInPushState = location.state?.userType || null;
  const userTypeInAuthInfo = isConfirm && authInfo?.userType ? authInfo?.userType : null;
  const userType = pathParams?.userType || userTypeInPushState || userTypeInAuthInfo || null;

  // Only the publicly offerable types reach the forms and the /signup/:userType
  // match below. A second type in Console would otherwise put a picker on the
  // public signup page and stop new accounts being assigned a type at all,
  // because the template only auto-assigns while exactly one type exists.
  //
  // An invite-only type gets through in two cases only: the visitor holds a
  // link whose token our server has just verified for this very type (see
  // loadData), or they are back from an identity provider whose round-trip
  // started from such a link. Then that one type is offered, alone, so the
  // form assigns it the way it assigns the sole public type. Anything else,
  // a guessed /signup/business included, stays a 404.
  const { userTypes: configuredUserTypes = [], userFields = [] } = config.user;
  const inviteToken = readInviteToken(location?.search);
  const inviteAdmitsType =
    !!userType &&
    isInviteOnlyUserType(userType) &&
    ((invite?.valid === true && invite.userType === userType && invite.token === inviteToken) ||
      (isConfirm && userTypeInAuthInfo === userType));
  const userTypes = inviteAdmitsType
    ? configuredUserTypes.filter(conf => conf.userType === userType)
    : publicUserTypes(configuredUserTypes);
  const preselectedUserType = userTypes.find(conf => conf.userType === userType)?.userType || null;
  const userTypeMaybe = preselectedUserType ? { userType: preselectedUserType } : {};
  const fromMaybe = from ? { from } : {};
  const show404 = userType && !preselectedUserType;

  const user = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!user.id;

  const prefilledEmail =
    readEmailFromSearch(location?.search) || (inviteAdmitsType ? invite?.email : null) || null;

  const marketplaceName = config.marketplaceName;
  const schemaTitle = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleLogin' }, { marketplaceName })
    : isOnboarding
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaTitleOnboarding' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaTitleSignup' }, { marketplaceName });
  const schemaDescription = isLogin
    ? intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionLogin' }, { marketplaceName })
    : intl.formatMessage({ id: 'AuthenticationPage.schemaDescriptionSignup' }, { marketplaceName });
  const topbarClasses = '';

  // The onboarding tab is the only auth tab that requires an authenticated
  // user; everywhere else we redirect away if the user is already signed in.
  // Post-signup users go to /get-started instead of straight to landing/from.
  // Confirm-tab signups (the SSO confirm-create-account flow for Vipps /
  // Google / Facebook) are also new accounts, so they get the same
  // onboarding redirect — without this, a successful confirm leaves the
  // user authenticated but stranded on /confirm.
  const shouldRedirectAfterSignup = isAuthenticated && currentUserLoaded && (isSignup || isConfirm);
  const shouldRedirectToFrom =
    isAuthenticated && from && !isOnboarding && !shouldRedirectAfterSignup;
  const shouldRedirectToLandingPage =
    isAuthenticated &&
    currentUserLoaded &&
    !isOnboarding &&
    !shouldRedirectAfterSignup &&
    (isWelcome || isLogin);
  // The welcome is for someone who just signed up; a signed-out visitor who
  // types the URL gets the login page instead of an empty one. Only once
  // mounted: the server renders without auth state, and a redirect there
  // would bounce a signed-in refresh of /get-started to the login page.
  const shouldRedirectToLoginFromOnboarding = isOnboarding && !isAuthenticated;

  // The welcome is a one-off. It is stamped on the profile the first time it
  // renders (onboardingNudgeShownAt); `onboardingActive` keeps this very
  // visit on screen while that stamp lands in the store, and any later visit
  // (a refresh, a returning user, a typed URL) goes to the homepage instead.
  const onboardingShownAt = user.attributes?.profile?.privateData?.onboardingNudgeShownAt;
  const [onboardingActive, setOnboardingActive] = useState(false);
  const showOnboarding = isOnboarding && isAuthenticated && currentUserLoaded;
  useEffect(() => {
    if (showOnboarding && !onboardingShownAt && !onboardingActive) {
      setOnboardingActive(true);
      onOnboardingNudgeShown();
    }
  }, [showOnboarding, onboardingShownAt, onboardingActive, onOnboardingNudgeShown]);
  const shouldRedirectOnboardingSeen = showOnboarding && !!onboardingShownAt && !onboardingActive;

  const onboardingLoading = isOnboarding && isAuthenticated && !currentUserLoaded;

  if (
    onboardingLoading ||
    (!mounted &&
      (shouldRedirectToLandingPage ||
        shouldRedirectAfterSignup ||
        shouldRedirectToLoginFromOnboarding))
  ) {
    return (
      <BlankPage
        schemaTitle={schemaTitle}
        schemaDescription={schemaDescription}
        topbarClasses={topbarClasses}
      />
    );
  }

  if (shouldRedirectAfterSignup) {
    // Where they were when they signed up rides along, so the welcome can
    // take them back there.
    return <NamedRedirect name="GetStartedPage" state={from ? { from } : {}} />;
  } else if (shouldRedirectToFrom) {
    return <Redirect to={from} />;
  } else if (shouldRedirectToLandingPage) {
    return <NamedRedirect name="LandingPage" />;
  } else if (shouldRedirectToLoginFromOnboarding) {
    return <NamedRedirect name="LoginPage" />;
  } else if (shouldRedirectOnboardingSeen) {
    return <NamedRedirect name="LandingPage" />;
  } else if (show404) {
    return <NotFoundPage staticContext={staticContext} />;
  }

  // ---- Action handlers ----

  // Welcome → Continue: route to /signup/complete with the email
  // pre-filled. Per product direction, we always assume new-user
  // intent here; an existing user gets a clear "email already taken"
  // error when they submit the full signup form on the next step.
  const handleWelcomeSubmit = ({ email }) => {
    const signupPath = pathByRouteName('SignupCompletePage', routeConfiguration);
    const search = email ? `?email=${encodeURIComponent(email)}` : '';
    history.push(`${signupPath}${search}`, { ...fromMaybe });
  };

  const getDataForSSORoutes = () => {
    const baseUrl = apiBaseUrl();
    const defaultReturn = pathByRouteName('LandingPage', routeConfiguration);
    const defaultConfirm = pathByRouteName('ConfirmPage', routeConfiguration);
    const queryParams = new URLSearchParams({
      ...(defaultReturn ? { defaultReturn } : {}),
      ...(defaultConfirm ? { defaultConfirm } : {}),
      ...(from ? { from } : {}),
      ...(preselectedUserType ? { userType: preselectedUserType } : {}),
    });
    return { baseUrl, queryParams: queryParams.toString() };
  };

  const onAuthWithFacebook = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    window.location.href = `${baseUrl}/api/auth/facebook?${queryParams}`;
  };
  const onAuthWithGoogle = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    window.location.href = `${baseUrl}/api/auth/google?${queryParams}`;
  };
  const onAuthWithVipps = () => {
    const { baseUrl, queryParams } = getDataForSSORoutes();
    window.location.href = `${baseUrl}/api/auth/vipps?${queryParams}`;
  };

  // ---- Cross-link helpers ----

  const emailSearchMaybe = prefilledEmail
    ? { search: `email=${encodeURIComponent(prefilledEmail)}` }
    : {};

  // Carry the post-auth "from" location across the welcome/login/signup
  // cross-links so that switching tabs (e.g. "Logg inn" from the
  // welcome screen) doesn't drop the route guard's redirect target.
  // Without this, an unauthenticated buyer routed via /checkout →
  // /signup → /login lands on the landing page after login.
  const crossLinkTo = (extra = {}) => (from ? { ...extra, state: { from } } : extra);

  // ---- Body ----

  const termsAndConditions = (
    <TermsAndConditions onOpenPrivacyPolicy={() => setPrivacyModalOpen(true)} intl={intl} />
  );

  const showFacebookLogin = !!process.env.REACT_APP_FACEBOOK_APP_ID;
  const showGoogleLogin = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
  const showVippsLogin = !!process.env.REACT_APP_VIPPS_CLIENT_ID;

  // Wrap the active panel in the white card on the grey-50 page bg.
  const card = body => (
    <div className={css.cardOuter}>
      <div className={css.card}>{body}</div>
    </div>
  );

  let body = null;

  if (isWelcome) {
    body = card(
      <>
        {/* "Opprett konto eller Logg inn". Anyone who is not logged in and
            opens a create-listing link lands here, and a good share of them
            already have an account (that is how they got the email). A
            heading that only says "create account" tells those people to
            make an account they already have. The login half is a real
            link, and it carries the `from` state so an existing member who
            logs in goes straight back to what they were opening. */}
        <Heading as="h1" rootClassName={classNames(css.cardTitle, css.cardTitleDual)}>
          <FormattedMessage
            id="AuthenticationPage.welcomeTitleDual"
            values={{
              signup: <FormattedMessage id="AuthenticationPage.welcomeTitle" />,
              // Kept together in one span so a narrow phone wraps the
              // heading as "Opprett konto" / "eller Logg inn" — never
              // between "Logg" and "inn".
              login: (
                <span className={css.cardTitleLoginPart}>
                  <span className={css.cardTitleOr}>
                    <FormattedMessage id="AuthenticationPage.welcomeTitleOr" />
                  </span>{' '}
                  <NamedLink
                    className={css.cardTitleLoginLink}
                    name="LoginPage"
                    to={crossLinkTo(emailSearchMaybe)}
                  >
                    <FormattedMessage id="AuthenticationPage.welcomeFootLogIn" />
                  </NamedLink>
                </span>
              ),
            }}
          />
        </Heading>
        <AuthenticationFormErrorMessage
          isLogin
          idpAuthError={authError}
          loginError={null}
          signupError={null}
        />
        <WelcomeForm
          onSubmit={handleWelcomeSubmit}
          showVippsLogin={showVippsLogin}
          showFacebookLogin={showFacebookLogin}
          showGoogleLogin={showGoogleLogin}
          onAuthWithVipps={onAuthWithVipps}
          onAuthWithFacebook={onAuthWithFacebook}
          onAuthWithGoogle={onAuthWithGoogle}
        />
        <p className={css.cardFootLink}>
          <FormattedMessage
            id="AuthenticationPage.welcomeFootLink"
            values={{
              link: (
                <NamedLink name="LoginPage" to={crossLinkTo(emailSearchMaybe)}>
                  <FormattedMessage id="AuthenticationPage.welcomeFootLogIn" />
                </NamedLink>
              ),
            }}
          />
        </p>
        {/* The privacy line. Accepting the terms happens on the next step
            (the sign-up form, or the Vipps / Google / Facebook confirm
            step), where the checkbox is. */}
        <p className={css.termsAck}>
          <FormattedMessage
            id="FieldTermsAcceptance.privacyNotice"
            values={{
              privacyLink: (
                <button
                  type="button"
                  className={css.termsAckLink}
                  onClick={() => setPrivacyModalOpen(true)}
                >
                  <FormattedMessage id="FieldTermsAcceptance.privacyLinkText" />
                </button>
              ),
            }}
          />
        </p>
      </>
    );
  } else if (isLogin) {
    body = card(
      <>
        <Heading as="h1" rootClassName={classNames(css.cardTitle, css.cardTitleLogin)}>
          <FormattedMessage id="AuthenticationPage.loginTitle" />
        </Heading>
        <AuthenticationFormErrorMessage
          isLogin
          idpAuthError={authError}
          loginError={loginError}
          signupError={null}
        />
        {/* Social buttons sit above the divider, mirroring the welcome
            page. Each provider is gated by its env client id and fires
            the matching /api/auth/<provider> route on the backend. */}
        <SocialAuthButtons
          vippsLabelId="AuthenticationPage.loginWithVipps"
          googleLabelId="AuthenticationPage.loginWithGoogle"
          facebookLabelId="AuthenticationPage.loginWithFacebook"
          showVippsLogin={showVippsLogin}
          showFacebookLogin={showFacebookLogin}
          showGoogleLogin={showGoogleLogin}
          onAuthWithVipps={onAuthWithVipps}
          onAuthWithFacebook={onAuthWithFacebook}
          onAuthWithGoogle={onAuthWithGoogle}
        />
        <div className={css.divider}>
          <span className={css.dividerText}>
            <FormattedMessage id="AuthenticationPage.or" />
          </span>
        </div>
        <LoginForm
          className={css.loginForm}
          onSubmit={submitLogin}
          inProgress={authInProgress}
          prefilledEmail={prefilledEmail}
        />
        <p className={css.cardFootLink}>
          <FormattedMessage
            id="AuthenticationPage.loginFootLink"
            values={{
              link: (
                <NamedLink name="SignupPage" to={crossLinkTo()}>
                  <FormattedMessage id="AuthenticationPage.loginFootSignup" />
                </NamedLink>
              ),
            }}
          />
        </p>
      </>
    );
  } else if (isSignup) {
    // A business invited to the pilot signs up as a Zuprize Partner: Guro's
    // badge centred at the top, and its own heading under it.
    const isBusinessSignup = preselectedUserType === BUSINESS_USER_TYPE;
    body = card(
      <>
        {isBusinessSignup ? (
          // Guro's badge with the heading close under it (Guro, 9 October):
          // "Opprett" on one line, "Zuprize Partnerkonto" unbroken on the
          // next, at every width.
          <div className={css.businessTitleGroup}>
            <PartnerBadge className={css.partnerBadge} />
            <Heading as="h1" rootClassName={css.cardTitle}>
              <FormattedMessage
                id="AuthenticationPage.signupTitleBusiness"
                values={{
                  br: <br />,
                  line: chunks => <span className={css.titleLine}>{chunks}</span>,
                }}
              />
            </Heading>
          </div>
        ) : (
          <Heading as="h1" rootClassName={css.cardTitle}>
            <FormattedMessage id="AuthenticationPage.signupTitle" />
          </Heading>
        )}
        <AuthenticationFormErrorMessage
          isLogin={false}
          idpAuthError={null}
          loginError={null}
          signupError={signupError}
        />
        <SignupForm
          className={css.signupForm}
          onSubmit={getHandleSubmitSignup({
            submitSignup,
            userFields,
          })}
          inProgress={authInProgress}
          termsAndConditions={termsAndConditions}
          preselectedUserType={preselectedUserType}
          userTypes={userTypes}
          userFields={userFields}
          prefilledEmail={prefilledEmail}
        />
        <p className={css.cardFootLink}>
          <FormattedMessage
            id="AuthenticationPage.signupFootLink"
            values={{
              link: (
                <NamedLink name="LoginPage" to={crossLinkTo(emailSearchMaybe)}>
                  <FormattedMessage id="AuthenticationPage.signupFootLogIn" />
                </NamedLink>
              ),
            }}
          />
        </p>
      </>
    );
  } else if (isConfirm) {
    body = card(
      <>
        <Heading as="h1" rootClassName={css.cardTitle}>
          <FormattedMessage id="AuthenticationPage.confirmSignupWithIdpTitle" values={{ idp }} />
        </Heading>
        <p className={css.cardSubtitle}>
          <FormattedMessage id="AuthenticationPage.confirmSignupInfoText" />
        </p>
        <AuthenticationFormErrorMessage
          isLogin={false}
          idpAuthError={null}
          loginError={null}
          signupError={confirmError}
        />
        <ConfirmSignupForm
          className={css.signupForm}
          inProgress={authInProgress}
          onSubmit={getHandleSubmitConfirm({
            authInfo,
            submitSingupWithIdp,
            userFields,
          })}
          authInfo={authInfo}
          idp={idp}
          preselectedUserType={preselectedUserType}
          userTypes={userTypes}
          userFields={userFields}
        />
      </>
    );
  }

  // The welcome is a dialog over the homepage (the route loads the
  // homepage's data), so a new member is already inside the site.
  if (showOnboarding) {
    const leaveWelcome = () => history.push(from || '/');
    return (
      <>
        <LandingPage />
        <OnboardingWelcome
          emailVerified={!!user.attributes?.emailVerified}
          isBusiness={isBusinessUser(user)}
          from={from}
          onDismiss={leaveWelcome}
        />
      </>
    );
  }

  // Fallback: a non-onboarding tab where the freshly signed-up user
  // hasn't verified their email yet. Keep the existing Sharetribe
  // EmailVerificationInfo flow available in case loadData lands here.
  const showEmailVerification =
    !isOnboarding && !isWelcome && !isLogin && !isConfirm
      ? currentUserLoaded && !user.attributes.emailVerified
      : false;

  if (showEmailVerification) {
    body = (
      <EmailVerificationInfo
        name={user.attributes.profile.firstName}
        email={<span className={css.email}>{user.attributes.email}</span>}
        onResendVerificationEmail={onResendVerificationEmail}
        resendErrorMessage={
          <ResendVerificationErrorMessage sendVerificationEmailError={sendVerificationEmailError} />
        }
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      />
    );
  }

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      // Login and sign-up are not pages to land on from Google (Guro saw them
      // as sitelinks under zuprize.no).
      noIndex
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutWrapperMain}
        topbar={<TopbarContainer className={topbarClasses} />}
        footer={<FooterContainer />}
      >
        <section className={css.pageRoot}>{body}</section>
      </LayoutSingleColumn>
      <Modal
        id="AuthenticationPage.privacyPolicy"
        isOpen={privacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        usePortal
        containerClassName={css.privacyModalContainer}
        onManageDisableScrolling={onManageDisableScrolling}
        focusElementId={'terms-accepted.tos-and-privacy'}
      >
        <div className={css.privacyWrapper} role="complementary">
          <PrivacyPolicyContent
            inProgress={pageAssetsFetchInProgress}
            error={pageAssetsFetchError}
            data={pageAssetsData?.[camelize(PRIVACY_POLICY_ASSET_NAME)]?.data}
            featuredListings={getFeaturedListingsProps(camelize(PRIVACY_POLICY_ASSET_NAME), props)}
            isOpen={privacyModalOpen}
          />
        </div>
      </Modal>
    </Page>
  );
};

/**
 * The AuthenticationPage "container" component.
 */
const AuthenticationPage = props => {
  const dispatch = useDispatch();

  const authInProgress = useSelector(state => authenticationInProgress(state));
  const currentUser = useSelector(state => state.user?.currentUser);
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const loginError = useSelector(state => state.auth?.loginError);
  const signupError = useSelector(state => state.auth?.signupError);
  const confirmError = useSelector(state => state.auth?.confirmError);
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  const sendVerificationEmailInProgress = useSelector(
    state => state.user?.sendVerificationEmailInProgress
  );
  const sendVerificationEmailError = useSelector(state => state.user?.sendVerificationEmailError);

  const hostedAssets = useSelector(state => state.hostedAssets || {});
  const pageAssetsData = hostedAssets.pageAssetsData;
  const pageAssetsFetchInProgress = hostedAssets.inProgress;
  const pageAssetsFetchError = hostedAssets.error;

  const featuredListingData = useSelector(state => state.featuredListings || {});
  const entities = useSelector(state => state.marketplaceData?.entities || {});
  const invite = useSelector(state => state.AuthenticationPage?.invite || null);

  const getListingEntitiesById = useCallback(
    listingIds => getListingsById({ marketplaceData: { entities } }, listingIds),
    [entities]
  );

  const submitLogin = useCallback(({ email, password }) => dispatch(login(email, password)), [
    dispatch,
  ]);
  const submitSignup = useCallback(params => dispatch(signup(params)), [dispatch]);
  const submitSingupWithIdp = useCallback(params => dispatch(signupWithIdp(params)), [dispatch]);
  const onResendVerificationEmail = useCallback(() => dispatch(sendVerificationEmail()), [
    dispatch,
  ]);
  const onOnboardingNudgeShown = useCallback(() => dispatch(markOnboardingNudgeShown()), [
    dispatch,
  ]);
  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );
  const onFetchFeaturedListings = useCallback(
    (sectionId, parentPage, listingImageConfig, allSections) =>
      dispatch(fetchFeaturedListings({ sectionId, parentPage, listingImageConfig, allSections })),
    [dispatch]
  );

  return (
    <AuthenticationPageComponent
      {...props}
      authInProgress={authInProgress}
      currentUser={currentUser}
      isAuthenticated={isAuthenticated}
      loginError={loginError}
      scrollingDisabled={scrollingDisabled}
      signupError={signupError}
      confirmError={confirmError}
      submitLogin={submitLogin}
      submitSignup={submitSignup}
      submitSingupWithIdp={submitSingupWithIdp}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      onResendVerificationEmail={onResendVerificationEmail}
      onOnboardingNudgeShown={onOnboardingNudgeShown}
      onManageDisableScrolling={onManageDisableScrolling}
      pageAssetsData={pageAssetsData}
      pageAssetsFetchInProgress={pageAssetsFetchInProgress}
      pageAssetsFetchError={pageAssetsFetchError}
      invite={invite}
      featuredListingData={featuredListingData}
      getListingEntitiesById={getListingEntitiesById}
      onFetchFeaturedListings={onFetchFeaturedListings}
    />
  );
};

export default AuthenticationPage;
